'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const preferences = [
    { id: 'ai-adoption', label: 'AI Adoption Strategies' },
    { id: 'change-management', label: 'Change Management' },
    { id: 'case-studies', label: 'Case Studies & Success Stories' },
    { id: 'webinars', label: 'Webinar Invitations' },
    { id: 'industry-insights', label: 'Industry Insights' },
  ];

  const handlePreferenceToggle = (id: string) => {
    setSelectedPreferences((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && selectedPreferences.length > 0) {
      setIsSubmitted(true);
      setTimeout(() => {
        setEmail('');
        setSelectedPreferences([]);
        setIsSubmitted(false);
      }, 3000);
    }
  };

  return (
    <div className="bg-gradient-to-br from-primary to-secondary rounded-lg p-8 lg:p-12 text-primary-foreground">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
          <Icon name="EnvelopeIcon" size={32} variant="outline" className="text-white" />
        </div>

        <h2 className="text-3xl lg:text-4xl font-bold font-headline mb-4">
          Stay Ahead of AI Transformation
        </h2>
        <p className="text-lg text-primary-foreground/90 mb-8">
          Get exclusive insights, practical guides, and early access to our latest resources delivered to your inbox.
        </p>

        {isSubmitted ? (
          <div className="bg-white/10 rounded-lg p-6 flex items-center justify-center space-x-3">
            <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-white" />
            <p className="text-lg font-semibold">Thank you for subscribing! Check your inbox for confirmation.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-left">Choose Your Content Preferences</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {preferences.map((pref) => (
                  <label
                    key={pref.id}
                    className="flex items-center space-x-3 cursor-pointer group"
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedPreferences.includes(pref.id)}
                        onChange={() => handlePreferenceToggle(pref.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                          selectedPreferences.includes(pref.id)
                            ? 'bg-white border-white' :'bg-transparent border-white/50 group-hover:border-white'
                        }`}
                      >
                        {selectedPreferences.includes(pref.id) && (
                          <Icon name="CheckIcon" size={14} variant="solid" className="text-primary" />
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-medium">{pref.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-6 py-3.5 rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button
                type="submit"
                disabled={selectedPreferences.length === 0}
                className="px-8 py-3.5 bg-accent text-accent-foreground rounded-md font-semibold font-cta hover:bg-accent/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <span>Subscribe Now</span>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </button>
            </div>

            <p className="text-xs text-primary-foreground/70">
              By subscribing, you agree to receive emails from AI Change Leaders. You can unsubscribe at any time.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default NewsletterSection;